import { useCallback, useMemo } from 'react';
import { useWidget } from 'state/widget/context';
import { useTrackingDispatch } from '../TrackingDispatchContext';
import { useTrackingState } from '../TrackingStateContext';
import { Trackers } from '../types';
import { isSponsored } from '../utils';

export default function useWidgetEventTracking() {
  const { dispatch } = useTrackingDispatch();
  const state = useTrackingState();

  const {
    state: { units = {} },
  } = useWidget();

  const trackWidgetInViewport: Trackers['trackWidgetInViewport'] =
    useCallback(() => {
      if (!state.getIsViewportTracked()) {
        state.trackWidgetInViewport();
        dispatch({ event: 'widgetInViewport' });
      }
    }, [dispatch, state]);

  const trackWidgetEngaged: Trackers['trackWidgetEngaged'] = useCallback(() => {
    if (!state.getIsEngagementTracked()) {
      state.trackEngagement();
      dispatch({ event: 'widgetEngaged' });
    }
    trackWidgetInViewport();
  }, [dispatch, state, trackWidgetInViewport]);

  const trackUnitClickThrough: Trackers['trackUnitClickThrough'] = useCallback(
    ({ resultId }) => {
      const event = isSponsored(resultId, units)
        ? 'spResultClickThrough'
        : 'resultClickThrough';

      dispatch({ event, payload: { resultId } });

      trackWidgetEngaged();
    },
    [dispatch, trackWidgetEngaged, units],
  );

  const trackUnitPlayed: Trackers['trackUnitPlayed'] = useCallback(
    ({ resultId }) => {
      if (!state.getIsUnitPlayed(resultId)) {
        state.trackUnitPlayed(resultId);

        const event = isSponsored(resultId, units)
          ? 'spResultPlayed'
          : 'resultPlayed';

        dispatch({ event, payload: { resultId } });
      }
      trackWidgetEngaged();
    },
    [dispatch, state, trackWidgetEngaged, units],
  );

  const trackWidgetLoad: Trackers['trackWidgetLoad'] = useCallback(() => {
    if (!state.getIsWidgetLoadTracked()) {
      state.trackWidgetLoad();
      dispatch({ event: 'widgetLoad' });
    }
  }, [dispatch, state]);

  return useMemo(
    () => ({
      trackUnitClickThrough,
      trackUnitPlayed,
      trackWidgetEngaged,
      trackWidgetInViewport,
      trackWidgetLoad,
    }),
    [
      trackUnitClickThrough,
      trackUnitPlayed,
      trackWidgetEngaged,
      trackWidgetInViewport,
      trackWidgetLoad,
    ],
  );
}
